import { OfferFragment } from '@occc/graphql';
import {
  FulfillmentChannel,
  getFulfillmentChannelInitialValues,
  transformAwardRestrictionsToFormValues,
} from '@occc/feat-constraints';
import { earningMechanismHelper } from '@occc-app/offerHelpers/utils/earningMechanismHelper';
import { awardHelper } from '@occc-app/offerHelpers/utils/awardHelper';
import { getPromptAtPumpInitialValues } from '@occc-app/routes/CampaignComponents/PromptAtPump/utils';
import { AwardVouchersWithExpirationDaysFormValues } from '@occc-app/offerHelpers/types';
import { transformCustomData } from '@occc-app/routes/CampaignComponents/components/CustomData/utils';
import { getIsOfferReturnableValue } from '@occc-app/routes/Reward/components/RewardConfigureForm/utils/getIsOfferReturnableValue';

import { getOfferNameAndDescriptionInitialValues } from '@occc-app/routes/Offer/components/OfferConfigureForm/utils/offerNameAndDescriptionInitialValues';
import { getOfferFulfillmentPriorityInitialValues } from '@occc-app/offerHelpers/utils/getOfferFulfillmentPriorityInitialValues';
import { getAwardCountInitialValues, LimitTimePeriod } from '@occc/feat-limits';
import { ConfigureFormInitialValues, ConfigureFormValues } from '../../../types';
import { OfferReturnStrategy } from '../../../../../dataAccessLayer/chain/types';

const transformEarningMechanismToFormValue = (
  earningMechanism: OfferFragment['earningMechanism'],
): Pick<ConfigureFormValues, 'pointSpend' | 'awardVouchers'> | {} => {
  if (!earningMechanism) {
    return {};
  }
  if (earningMechanismHelper.isPointSpend(earningMechanism)) {
    return {
      pointSpend: {
        id: earningMechanism.id,
        pointCost: earningMechanism.pointCost,
        allowedForNewMember: earningMechanism.allowedForNewMember ?? false,
      },
      awardVouchers: awardHelper.isVouchersAward(earningMechanism.pointSpendAward)
        ? {
            voucheredOffer: earningMechanism.pointSpendAward.voucheredOffer,
            voucherExpirationDays:
              earningMechanism.pointSpendAward.voucherExpirationDays || undefined,
            isEnabled: !!earningMechanism.pointSpendAward.voucherExpirationDays,
          }
        : {},
    };
  }

  return {};
};
type GetOfferConfigureFormInitialValuesParams = {
  offerReturnDefaultStrategy: boolean;
};

export const getConfigureFormInitialValues = ({
  offerReturnDefaultStrategy,
}: GetOfferConfigureFormInitialValuesParams): ConfigureFormInitialValues => ({
  awardVouchers: {
    isEnabled: false,
  } as AwardVouchersWithExpirationDaysFormValues,
  awardCount: {
    global: {
      isEnabled: false,
      timePeriod: LimitTimePeriod.Total,
    },
    membership: {
      isEnabled: false,
      timePeriod: LimitTimePeriod.Total,
    },
  },
  promptAtPump: {
    isEnabled: false,
    shortPrompt: '',
    longPrompt: '',
  },
  isOfferReturnable: offerReturnDefaultStrategy,
  awardRestrictions: { isEnabled: false, awards: [] },
  fulfillmentChannelConstraint: {
    isEnabled: false,
    channel: FulfillmentChannel.OrderAheadOnly,
  },
});

type TransformOfferToInitialValuesParams = {
  offerReturnStrategy: OfferReturnStrategy;
  offerReturnDefaultStrategy: boolean;
};

export const getTransformOfferToInitialValues =
  ({ offerReturnStrategy, offerReturnDefaultStrategy }: TransformOfferToInitialValuesParams) =>
  (offer?: OfferFragment): ConfigureFormInitialValues => {
    const values = getConfigureFormInitialValues({ offerReturnDefaultStrategy });

    if (!offer) {
      return values;
    }

    const awardRestrictionsConstraint = offer.constraints.find(
      (constraint) => constraint.__typename === 'ConstraintPurchasableEligibility',
    );

    return {
      ...values,
      ...getOfferNameAndDescriptionInitialValues(offer),
      ...getOfferFulfillmentPriorityInitialValues(offer),
      ...transformEarningMechanismToFormValue(offer.earningMechanism),
      awardCount: getAwardCountInitialValues(offer, values.awardCount),
      promptAtPump: getPromptAtPumpInitialValues(offer).promptAtPump,
      customData: transformCustomData(offer.customData),
      isOfferReturnable: getIsOfferReturnableValue(
        offerReturnStrategy,
        offer,
        offerReturnDefaultStrategy,
      ),
      awardRestrictions: awardRestrictionsConstraint
        ? transformAwardRestrictionsToFormValues(awardRestrictionsConstraint)
        : values.awardRestrictions,
      fulfillmentChannelConstraint: getFulfillmentChannelInitialValues(offer),
    };
  };
